import { Component } from '@theme/component';

/**
 * @typedef {Object} AxExpandableCardsRefs
 * @property {HTMLUListElement} list
 * @property {HTMLElement & { swiper?: any; initialize?: () => void }} swiper
 */

/**
 * Expandable Cards section — refonte 2026.
 *
 * Pattern (ax-athletes horizontal-like) :
 *   - <ul> rendu côté serveur avec les cards en <li> = layout desktop natif,
 *     zéro Swiper, zéro CLS.
 *   - <swiper-container init="false"> rendu VIDE = juste un wrapper en attente.
 *   - En MOBILE (max-width: 1199px) : JS déplace le contenu des <li> dans des
 *     <swiper-slide> au sein du <swiper-container>, puis init Swiper.
 *   - En DESKTOP : tout reste dans <ul>, Swiper jamais init.
 *   - Au crossover de breakpoint : mount / unmount mobile.
 *
 * Desktop interactions :
 *   - Hover sur un <li> → swap classe .is-active (le mouseenter).
 *   - CSS gère la transition (flex, height de desc, opacity, font-size).
 *
 * Min-height (anti-CLS) :
 *   - CSS pose --ax-ec-card-min-height = 226px iso-maquette.
 *   - JS l'augmente uniquement si nécessaire (only-grow → pas de shift down).
 *
 * @extends {Component<AxExpandableCardsRefs>}
 */
class AxExpandableCards extends Component {
  /** @type {MediaQueryList} */
  #mql;
  /** @type {() => void} */
  #onBreakpointChange;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {boolean} */
  #isMobileMounted = false;

  connectedCallback() {
    super.connectedCallback();

    // Calcul anti-CLS SYNCHRONE (avant le 1er paint si possible) sur desktop.
    // Si getBoundingClientRect retourne 0 (layout pas prêt), early return — le
    // requestAnimationFrame plus bas retry.
    this.#mql = window.matchMedia('(max-width: 1199px)');
    if (!this.#mql.matches) this.#syncContentHeight();

    this.#initActiveSync();
    this.#initAsync();
  }

  async #initAsync() {
    await customElements.whenDefined('swiper-container');
    if (!this.isConnected) return;

    this.#onBreakpointChange = () => this.#syncMobileMount();
    this.#mql.addEventListener('change', this.#onBreakpointChange);
    this.#syncMobileMount();

    // Retry calc après layout pass (au cas où le sync call était trop tôt).
    requestAnimationFrame(() => {
      this.#syncContentHeight();
      this.#observeResize();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mql?.removeEventListener('change', this.#onBreakpointChange);
    this.#resizeObserver?.disconnect();
    const sw = this.refs.swiper?.swiper;
    if (sw && !sw.destroyed) sw.destroy(true, true);
  }

  // ============ MOBILE MOUNT / UNMOUNT ============

  #syncMobileMount() {
    if (this.#mql.matches) this.#mountMobile();
    else this.#unmountMobile();
  }

  /** Move children content from <li> into <swiper-slide>s and init Swiper. */
  #mountMobile() {
    if (this.#isMobileMounted) return;
    const { list, swiper } = this.refs;
    if (!list || !swiper) return;

    for (const li of [...list.children]) {
      const slide = document.createElement('swiper-slide');
      slide.className = li.className;
      while (li.firstChild) slide.appendChild(li.firstChild);
      swiper.appendChild(slide);
    }
    list.replaceChildren();

    const paginationEl = this.querySelector('.ax-expandable-cards__pagination');
    Object.assign(swiper, {
      slidesPerView: 1,
      spaceBetween: 8,
      grabCursor: true,
      a11y: { enabled: true },
      watchOverflow: true,
      pagination: paginationEl ? { el: paginationEl, clickable: true } : false,
    });
    swiper.initialize?.();
    this.#isMobileMounted = true;

    document.dispatchEvent(new CustomEvent('reactivateLazyObserver'));
  }

  /** Destroy Swiper, move content back into <li>s. */
  #unmountMobile() {
    if (!this.#isMobileMounted) return;
    const { list, swiper } = this.refs;
    if (!list || !swiper) return;

    const sw = swiper.swiper;
    if (sw && !sw.destroyed) sw.destroy(true, true);

    for (const slide of [...swiper.children]) {
      const li = document.createElement('li');
      li.className = slide.className;
      while (slide.firstChild) li.appendChild(slide.firstChild);
      list.appendChild(li);
    }
    swiper.replaceChildren();
    this.#isMobileMounted = false;
  }

  // ============ DESKTOP ACTIVE SYNC (hover swap) ============

  #initActiveSync() {
    const list = this.refs.list;
    if (!list) return;

    list.addEventListener('mouseover', (e) => {
      if (this.#mql?.matches) return;
      const slide = /** @type {HTMLElement | null} */ (e.target instanceof Element ? e.target.closest('.ax-expandable-cards__slide') : null);
      if (!slide || slide.classList.contains('is-active')) return;
      for (const s of list.children) s.classList.remove('is-active');
      slide.classList.add('is-active');
    });
  }

  // ============ MIN-HEIGHT CALC (anti-CLS, only-grow) ============

  #syncContentHeight() {
    if (this.#mql?.matches) return; // mobile : Swiper gère, pas besoin
    const list = this.refs.list;
    if (!list) return;

    const slides = [...list.querySelectorAll('.ax-expandable-cards__slide')];
    if (!slides.length) return;
    const activeSlide = list.querySelector('.ax-expandable-cards__slide.is-active') ?? slides[0];
    if (!activeSlide) return;

    const activeDesc = /** @type {HTMLElement | null} */ (activeSlide.querySelector('.ax-expandable-card__description'));
    if (!activeDesc) return;
    const descWidth = activeDesc.getBoundingClientRect().width;
    if (descWidth === 0) return;

    this.style.setProperty('--ax-ec-desc-width', `${descWidth}px`);

    const measureBox = document.createElement('div');
    measureBox.style.cssText = `
      position: absolute;
      top: -9999px;
      left: -9999px;
      width: ${descWidth}px;
      visibility: hidden;
      pointer-events: none;
    `;
    this.appendChild(measureBox);

    const gap = 16;
    const padding = 32;
    let maxHeight = 0;

    for (const slide of slides) {
      const title = /** @type {HTMLElement | null} */ (slide.querySelector('.ax-expandable-card__title'));
      const desc = /** @type {HTMLElement | null} */ (slide.querySelector('.ax-expandable-card__description'));
      if (!title) continue;

      const titleClone = /** @type {HTMLElement} */ (title.cloneNode(true));
      titleClone.style.fontSize = 'var(--font-size--h3)';
      titleClone.style.lineHeight = 'var(--font-h3--line-height)';
      measureBox.appendChild(titleClone);
      const titleH = titleClone.offsetHeight;
      titleClone.remove();

      const descH = desc?.scrollHeight ?? 0;
      const total = titleH + (descH > 0 ? gap + descH : 0) + padding;
      if (total > maxHeight) maxHeight = total;
    }

    measureBox.remove();

    if (maxHeight > 0) {
      const currentMin = parseFloat(getComputedStyle(this).getPropertyValue('--ax-ec-card-min-height')) || 0;
      if (maxHeight > currentMin) {
        this.style.setProperty('--ax-ec-card-min-height', `${maxHeight}px`);
      }
    }
  }

  #observeResize() {
    let lastWidth = this.offsetWidth;
    this.#resizeObserver = new ResizeObserver(() => {
      if (this.offsetWidth === lastWidth) return;
      lastWidth = this.offsetWidth;
      this.#syncContentHeight();
    });
    this.#resizeObserver.observe(this);
  }
}

customElements.define('ax-expandable-cards', AxExpandableCards);
